import { useEffect, useState } from 'react';
import { sharingApi } from '../api/sharing';

let inboundIdsCache: Promise<Set<number>> | null = null;

export function clearSharedMemberAccessCache(): void {
  inboundIdsCache = null;
}

async function fetchInboundIds(): Promise<Set<number>> {
  if (!inboundIdsCache) {
    inboundIdsCache = sharingApi
      .listInboundMembers()
      .then((rows) => new Set(rows.map((r) => r.member_id)))
      .catch((err) => {
        // Allow a retry on next mount if the request failed.
        inboundIdsCache = null;
        throw err;
      });
  }
  return inboundIdsCache;
}

/**
 * Returns whether the current viewer reached `memberId` via a direct
 * member-level share — i.e. the entire family was NOT shared with them.
 *
 * Three states matter for the UI:
 *   - `true`  → user has access ONLY to this one member; "family" links
 *               must point at `/shared-members` (the family page would
 *               403 / hide everything).
 *   - `false` → user is the family owner OR a family-share recipient;
 *               regular `/families/{id}` links are correct.
 *   - `null`  → still resolving; render the safe (regular family) link
 *               so navigation isn't gated on this fetch finishing.
 *
 * The inbound-member list is cached per session via a module-level
 * promise (it's the same list for every member viewed in one session).
 * Call `clearSharedMemberAccessCache()` after accepting / rotating a
 * share to force a refresh.
 */
export function useViewedAsSharedMember(memberId: number | null | undefined): boolean | null {
  const [state, setState] = useState<boolean | null>(null);
  useEffect(() => {
    if (memberId == null) {
      setState(null);
      return;
    }
    let cancelled = false;
    fetchInboundIds()
      .then((set) => {
        if (!cancelled) setState(set.has(memberId));
      })
      .catch(() => {
        // Fail-open: if the inbound list can't be fetched, behave as
        // if the member is NOT shared (regular family links). Worse
        // case the user clicks into a family they can't see and we
        // surface the existing 403 / empty state — no broken nav.
        if (!cancelled) setState(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId]);
  return state;
}
